import type { Storage } from 'tradera/utils/storage';
import { localStore } from 'tradera/utils/storage';

type ResultSetRow<T> = {
    key: string;
    value: T;
    timestamp: string;
    expires: string;
};

type ResultSetResult<T> = ResultSetRow<T>[] & {
    orderByTimestamp?: () => ResultSetResult<T>;
    orderByValue?: () => ResultSetResult<T>;
    orderByExpire?: () => ResultSetResult<T>;
};

class ResultSet<T> {
    private _result: ResultSetResult<T>;
    constructor(result: ResultSetRow<T>[]) {
        this._result = result;
        this._result.orderByTimestamp = this.orderByTimestamp.bind(this);
        this._result.orderByValue = this.orderByValue.bind(this);
        this._result.orderByExpire = this.orderByExpire.bind(this);
    }

    get() {
        return this._result;
    }

    _orderBy(orderProp: keyof ResultSetRow<T>) {
        function orderByProp(a: ResultSetRow<T>, b: ResultSetRow<T>) {
            if (a[orderProp] === b[orderProp]) {
                return 0;
            }

            return a[orderProp] < b[orderProp] ? 1 : -1;
        }

        return this._result.sort(orderByProp);
    }

    orderByTimestamp() {
        return this._orderBy('timestamp');
    }

    orderByValue() {
        return this._orderBy('value');
    }

    orderByExpire() {
        return this._orderBy('expires');
    }
}

type StorageServiceOptions = {
    datastore?: Storage;
};

// KATS: since this is TS now we can likely make a couple of internal functions etc "private"...that will make it more useful from a TS perspective (public api etc) and something that C# devs can relate to I guess ;)
class StorageService {
    namespace: string;
    datastore: Storage;
    constructor(namespace = 'default', options: StorageServiceOptions = {}) {
        this.namespace = namespace;
        this.datastore = options.datastore || localStore;
    }

    getDataSafe() {
        return this.datastore.getItem(this.namespace) || {};
    }

    store(data: unknown) {
        this.datastore.setItem(this.namespace, data);
        return true;
    }

    remove(key: string) {
        const data = this.getDataSafe(),
            encodedKey = encodeURI(key).toLowerCase();

        this.purge(encodedKey, data);
    }

    purge(key: string, data: Record<string, unknown>) {
        delete data[key];
        this.store(data);
        return true;
    }

    get<TValue>(key: string) {
        const data = this.getDataSafe(),
            encodedKey = encodeURI(key).toLowerCase();

        if (
            data[encodedKey] &&
            data[encodedKey].expires &&
            data[encodedKey].expires < new Date().getTime()
        ) {
            this.purge(encodedKey, data);
        }

        return ((data[encodedKey] && data[encodedKey].value) as TValue) ?? null;
    }

    set(key: string, value: unknown, expires?: Date, timestamp?: Date) {
        const data = this.getDataSafe();
        const encodedKey = encodeURI(key).toLowerCase();

        const newData = {
            expires: (expires && expires.getTime()) || null,
            timestamp:
                (timestamp && timestamp.getTime()) || new Date().getTime(),
            value: value
        };

        this.datastore.setItem(this.namespace, {
            ...data,
            [encodedKey]: newData
        });

        return newData || null;
    }

    // KATS: can this removed?
    removeExpired() {
        const data = this.getDataSafe();

        for (const prop in data) {
            if (data.hasOwnProperty(prop)) {
                if (
                    data[prop].expires &&
                    data[prop].expires < new Date().getTime()
                ) {
                    this.purge(prop, data);
                }
            }
        }
    }

    fetch<T>(): ResultSetResult<T> {
        const data = this.getDataSafe();
        const rows: ResultSetRow<T>[] = [];

        for (const prop in data) {
            if (data.hasOwnProperty(prop)) {
                rows.push({
                    key: decodeURI(prop),
                    value: data[prop].value,
                    timestamp: data[prop].timestamp,
                    expires: data[prop].expires
                });
            }
        }

        return new ResultSet<T>(rows).get();
    }

    flush() {
        return this.datastore.clearByPrefix(this.namespace);
    }
}

export default StorageService;
